/**
 * Authorization utilities
 * Check user permissions and roles based on SmartField policies
 */

import { AuthPolicy, AuthUser, Role, SmartFieldClaimType } from './types';

/**
 * Check if user has a specific role
 */
export function hasRole(user: AuthUser | null, role: string): boolean {
  if (!user) return false;
  return user.roles.includes(role);
}

/**
 * Check if user has admin role
 */
export function isAdmin(user: AuthUser | null): boolean {
  return hasRole(user, Role.Admin);
}

/**
 * Check if user has a SmartFieldClaimType claim (any site)
 */
export function hasClaim(user: AuthUser | null, claimType: SmartFieldClaimType): boolean {
  if (!user) return false;
  if (isAdmin(user)) return true;
  const siteIds = user.sitePermissions.get(claimType);
  return siteIds !== undefined && siteIds.length > 0;
}

/**
 * Check if user has a SmartFieldClaimType claim for a specific site
 */
export function hasClaimForSite(
  user: AuthUser | null, 
  claimType: SmartFieldClaimType, 
  siteId: number
): boolean {
  if (!user) return false;
  if (isAdmin(user)) return true;
  
  const siteIds = user.sitePermissions.get(claimType);
  return siteIds ? siteIds.includes(siteId) : false;
}

/**
 * Get all site IDs user has permission for with a specific claim type
 * Note: For admins, this returns an empty array because admins have access to ALL sites.
 * Use isAdmin() check separately when you need to grant access to all sites.
 */
export function getPermittedSiteIds(
  user: AuthUser | null,
  claimType: SmartFieldClaimType
): number[] {
  if (!user) return [];
  // Note: Admins have access to all sites, but we can't return all site IDs here
  // because we don't have that information. Check isAdmin() separately.
  return user.sitePermissions.get(claimType) || [];
}

/**
 * Get all sites the user has any access to
 */
export function getAffiliatedSiteIds(user: AuthUser | null): number[] {
  if (!user) return [];
  return user.affiliatedSiteIds;
}

/**
 * Check if user is a user manager (has role or any UserManager claim)
 */
export function isUserManager(user: AuthUser | null): boolean {
  if (!user) return false;
  return isAdmin(user) || hasRole(user, Role.UserManager) || hasClaim(user, SmartFieldClaimType.UserManager);
}

/**
 * Check if user is a user manager for a specific site
 */
export function isUserManagerForSite(user: AuthUser | null, siteId: number): boolean {
  if (!user) return false;
  return isAdmin(user) || hasClaimForSite(user, SmartFieldClaimType.UserManager, siteId);
}

/**
 * Check if user is a site manager (any site)
 */
export function isSiteManager(user: AuthUser | null): boolean {
  if (!user) return false;
  return isAdmin(user) || hasClaim(user, SmartFieldClaimType.SiteManager);
}

/**
 * Check if user is a site manager for a specific site
 */
export function isSiteManagerForSite(user: AuthUser | null, siteId: number): boolean {
  if (!user) return false;
  return isAdmin(user) || hasClaimForSite(user, SmartFieldClaimType.SiteManager, siteId);
}

/**
 * Check if user is a trial manager for a specific trial/site
 */
export function isTrialManagerForSite(user: AuthUser | null, siteId: number): boolean {
  if (!user) return false;
  return isAdmin(user) || hasClaimForSite(user, SmartFieldClaimType.TrialManager, siteId);
}

/**
 * Check if user can read raw data (any site)
 */
export function isRawDataReader(user: AuthUser | null): boolean {
  if (!user) return false;
  return isAdmin(user) || 
         hasClaim(user, SmartFieldClaimType.RawDataReader) ||
         hasClaim(user, SmartFieldClaimType.RawDataEditor) ||
         hasClaim(user, SmartFieldClaimType.RawDataExporter);
}

/**
 * Check if user can read raw data for a specific site
 */
export function isRawDataReaderForSite(user: AuthUser | null, siteId: number): boolean {
  if (!user) return false;
  return isAdmin(user) ||
         hasClaimForSite(user, SmartFieldClaimType.RawDataReader, siteId) ||
         hasClaimForSite(user, SmartFieldClaimType.RawDataEditor, siteId) ||
         hasClaimForSite(user, SmartFieldClaimType.RawDataExporter, siteId);
}

/**
 * Check if user can read flux data for a specific site
 */
export function isFluxDataReaderForSite(user: AuthUser | null, siteId: number): boolean {
  if (!user) return false;
  return isAdmin(user) ||
         hasClaimForSite(user, SmartFieldClaimType.FluxDataReader, siteId) ||
         hasClaimForSite(user, SmartFieldClaimType.FluxDataEditor, siteId) ||
         hasClaimForSite(user, SmartFieldClaimType.FluxDataExporter, siteId);
}

/**
 * Check if user has permission based on SmartField authorization policies
 * Maps to policies defined in SmartField.Authorization/Policies.cs
 */
export function hasPermission(user: AuthUser | null, policy: AuthPolicy): boolean {
  if (!user) return false;

  switch (policy) {
    case AuthPolicy.UserManager:
      return isUserManager(user);

    case AuthPolicy.SiteManager:
      return isSiteManager(user);

    default:
      return false;
  }
}

/**
 * Check if user has permission for a specific site
 */
export function hasPermissionForSite(
  user: AuthUser | null, 
  policy: AuthPolicy, 
  siteId: number
): boolean {
  if (!user) return false;

  switch (policy) {
    case AuthPolicy.UserManager:
      return isUserManagerForSite(user, siteId);

    case AuthPolicy.SiteManager:
      return isSiteManagerForSite(user, siteId);

    default:
      return false;
  }
}

/**
 * Get user's claim values by claim type (generic claim access)
 */
export function getClaimValues(user: AuthUser | null, claimType: string): string[] {
  if (!user) return [];
  return user.claims.get(claimType) || [];
}
